import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import {useRouter} from 'next/router';
import cx from 'classnames';
import _ from 'lodash';

import LessonContainer from './LessonContainer';
import Placeholder from './Placeholder';
import InteractiveRubric from './InteractiveRubric';
import QuizLesson from './QuizLesson';
import InteractiveLesson from './InteractiveLesson';
import Markdown from './LessonMarkdown';
import LessonPaywall from './LessonPaywall';
import SignupWall from './SignupWall';
import LessonCommentSection from './LessonCommentSection';
import CourseLessonsProvider, {CourseLessonsContext} from '../../components/CourseLessonsProvider';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button, {ButtonTypes, ButtonSizes} from '../../components/ButtonNew';
import {Badge, DifficultyToBadge, BadgeColors} from '../../components/Badge';
import AuthorCard from '../../components/AuthorCard';
import DocumentHeader from '../../components/DocumentHeader';
import RatingWidget from '../../components/RatingWidget';
import YouTubeVideo from '../../components/YouTubeVideo';
import {StarIcon, SimpleLockIcon} from '../../components/svg';
import TableOfContents, {generateTOC} from '../../components/TableOfContents';
import {getNextAvailableLesson, getCanonicalUrl, getNextLessonPath} from '../../util/courses';
import {VIDEO_PREVIEW_URL} from '../../constants/content';
import {useAppContext, useAnalytics, useScrollToHash} from '../../hooks';
import {
  getLesson, rateLesson, saveLesson, getCommentsForQuestion,
} from '../../api/api';

const TopControls = ({
  lesson, breadcrumbs, showInteractiveElements, onClickSaveLesson,
}) => (
  <div className="flex justify-between">
    <Breadcrumbs items={breadcrumbs} className="mr-2" />
    {showInteractiveElements && (
      <span className="hidden sm:inline max-w-min relative">
        <Button
          buttonType={ButtonTypes.SECONDARY}
          onClick={onClickSaveLesson}
          aria-label="save"
          className="text-gray-400 rounded-lg"
        >
          <StarIcon
            className={cx('h-5 w-5 mr-1 -ml-1', {
              'text-yellow-400': lesson.savedAt,
              'text-gray-300': !lesson.savedAt,
            })}
          />
          {lesson.savedAt ? 'Saved' : 'Save'}
        </Button>
      </span>
    )}
  </div>
);

const LessonPage = ({
  trackSlug, courseSlug, lessonSlug, initialDraft, preview,
}) => {
  const {courses, setCurrentLesson, markLastVisit} = useContext(CourseLessonsContext);
  const {
    currentUser, hasSubscription, referrer, dispatch,
  } = useAppContext();
  const {tracker} = useAnalytics();
  const router = useRouter();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState({root: []});
  const [toc, setTOC] = useState();
  const [error, setError] = useState({status: false});
  const [showBottomComments, setShowBottomComments] = useState(true);
  const lessonContainerRef = useRef();
  const lessonCommentSectionRef = useRef();

  useEffect(() => {
    setShowBottomComments(true);
    const loadLesson = async () => {
      try {
        const currLesson = await getLesson(lessonSlug, courseSlug, trackSlug, referrer, preview);
        setLesson(currLesson);

        if (!preview) {
          tracker.lessonView({
            lesson: lessonSlug,
            course: courseSlug,
            track: trackSlug,
            isCodeProblem: Boolean(currLesson.codeProblem),
          });
        }

        if (courseSlug && lessonSlug) {
          markLastVisit(lessonSlug, courseSlug, trackSlug);
        }

        // Clear referrer for the next time a lesson is viewed
        dispatch({type: 'clearReferrer', payload: null});
        setCurrentLesson(currLesson);
      } catch (err) {
        router.push('/404');
      }
    };

    loadLesson();
  }, [trackSlug, courseSlug, lessonSlug]);

  useEffect(() => {
    const getComments = async () => {
      if (lesson?.questionId) {
        const c = await getCommentsForQuestion(lesson.questionId.toString());
        if (c) {
          _.remove(c.root, 'data.expert');
          setComments(c);
        }
      }
    };

    getComments();
  }, [lesson]);

  useEffect(() => {
    if (initialDraft) {
      lessonCommentSectionRef.current?.scrollIntoView({behavior: 'smooth'});
    }
  }, [initialDraft]);

  // Wait for lesson to load before applying hash/anchor scroll
  useScrollToHash(loading);

  // Latter check is to avoid rendering the page with stale state
  if (!lesson || lesson.slug !== lessonSlug) return <Placeholder />;

  const course = courses?.find((c) => c.slug === courseSlug);
  const track = courses?.find((t) => t.slug === trackSlug);
  const module = course?.modules?.find((m) => m.lessons.find((l) => l.slug === lessonSlug));

  // Only possible if client side navigated to a lesson with incorrect course or track slug
  if (!preview && (!course || (trackSlug && !track))) {
    router.push('/404');
    return null;
  }

  const courseList = trackSlug ? track.courses : [course];
  const nextLessonPath = preview
    ? null : getNextLessonPath(courseList, lessonSlug, courseSlug, trackSlug);
  const {hasAccess} = lesson;
  const isQuizLesson = lesson.lessonType === 'quiz';
  const isInteractiveLesson = lesson.lessonType === 'question';
  const showSignupWall = lesson.free && !hasAccess;
  const showPaywall = !lesson.free && !hasAccess;
  const showInteractiveElements = currentUser && hasAccess;

  let breadcrumbs;
  if (preview) {
    breadcrumbs = [];
  } else if (track) {
    breadcrumbs = [
      {name: 'Courses', url: '/courses'},
      {name: track.shortTitle, url: `/courses/${trackSlug}`, truncate: true},
      {name: course.shortTitle, url: `/courses/${trackSlug}#${courseSlug}`, truncate: true}, // TODO: Support URL fragments
    ];
  } else {
    breadcrumbs = [
      {name: 'Courses', url: '/courses'},
      {name: course.shortTitle, url: `/courses/${courseSlug}`},
    ];
  }

  if (module) {
    breadcrumbs.push({name: module.title, url: `/courses/${trackSlug}#${courseSlug}_${module.slug}`, truncate: true});
  }

  const onClickRateLesson = (rating) => {
    rateLesson(lessonSlug, {rating});
    tracker.lessonClickVote({vote: rating, lesson: lessonSlug, id: lessonSlug});
  };

  const onClickSubmitFeedback = (feedback) => {
    rateLesson(lessonSlug, {feedback, url: window.location.href});
    tracker.lessonClickSubmitFeedback({lesson: lessonSlug, id: lessonSlug});
  };

  const onClickSaveLesson = async () => {
    try {
      await saveLesson(lessonSlug, !lesson.savedAt);
      setLesson({...lesson, savedAt: !lesson.savedAt});
      tracker.lessonClickSave({lesson: lessonSlug});
    } catch (err) {
      setError({status: true, message: 'An error occurred. Please try again later'});
    }
  };

  const onClickNextLesson = () => {
    tracker.lessonClickNext({
      currentLesson: lessonSlug,
      nextLesson: nextLessonPath,
      course: courseSlug,
      track: trackSlug,
    });
  };

  const onMarkdownRendered = () => {
    setTOC(generateTOC());
    setLoading(false);
  };

  // Only show if not interactive lesson & if at least two sections
  const showToC = !isInteractiveLesson && toc?.length > 1;

  return (
    <div
      className="LessonPage w-full max-h-screen min-h-screen overflow-auto"
      ref={lessonContainerRef}
    >
      <DocumentHeader
        title={lesson.title}
        isFree={lesson.free}
        imgPath={_.get(lesson, 'image.url', lesson.image)}
        imgAlt={lesson.title}
        metaDescription={lesson.description}
        socialDescription={lesson.description}
        canonicalUrl={getCanonicalUrl(courseSlug, lesson.slug)}
      />
      <div className="w-full pt-3 px-4 relative sm:px-8 sm:mb-32 relative mx-auto">

        {/* Top controls IF video present */}
        {(lesson.video || lesson.paywalledVideo) && !isInteractiveLesson && (
          <div className="mb-5 max-w-5xl mx-auto">
            <TopControls
              lesson={lesson}
              breadcrumbs={breadcrumbs}
              showInteractiveElements={showInteractiveElements}
              onClickSaveLesson={onClickSaveLesson}
            />
          </div>
        )}

        {/* Lesson video */}
        {lesson.video && !isInteractiveLesson && !isQuizLesson && (
          <div className="rounded overflow-hidden max-w-5xl mx-auto mb-6">
            <YouTubeVideo
              videoID={lesson.video}
              onView={() => tracker.lessonViewVideo({lesson: lessonSlug, course: courseSlug})}
              onPlay={() => tracker.lessonPlayVideo({lesson: lessonSlug, course: courseSlug})}
            />
          </div>
        )}

        <div className={cx({
          hidden: lesson.paywalledVideo,
          'lg:flex lg:justify-between': showToC,
        })}
        >
          <div className="max-w-3xl mx-auto">

            {/* Top controls IF video NOT present */}
            {(!(lesson.video || lesson.paywalledVideo) || isInteractiveLesson) && (
              <TopControls
                lesson={lesson}
                breadcrumbs={breadcrumbs}
                showInteractiveElements={showInteractiveElements}
                onClickSaveLesson={onClickSaveLesson}
              />
            )}

            {/* Lesson title and author */}
            <div className={cx({'px-3': lesson.paywalledVideo})}>
              <div className={cx('flex flex-wrap justify-start items-center mb-4 gap-2', lesson.difficulty ? 'mt-2' : 'mt-6')}>
                <h1 className="text-2xl md:text-3xl font-bold text-black self-center">
                  {lesson.shortTitle}
                </h1>
                <div className="flex items-center gap-x-2">
                  {lesson.difficulty && (
                    <Badge color={DifficultyToBadge[lesson.difficulty]?.color}>
                      {DifficultyToBadge[lesson.difficulty]?.text}
                    </Badge>
                  )}
                  {!lesson.free && !hasSubscription && (
                    <Badge color={BadgeColors.GRAY} className="flex items-center">
                      <SimpleLockIcon className="h-3 w-3 mr-0.5" />
                      Premium
                    </Badge>
                  )}
                </div>
              </div>
              {lesson.author && (
                <AuthorCard author={lesson.author} className="mt-4 mb-6" />
              )}
            </div>

            {/* Typeform quiz lesson content */}
            {isQuizLesson && hasAccess && <QuizLesson lesson={lesson} />}

            {/* Lesson content */}
            {!isInteractiveLesson && <h3 className="my-4 text-lg text-gray-500 font-medium leading-tight">{lesson.description}</h3>}
            {isInteractiveLesson
              ? (
                <InteractiveLesson
                  lesson={lesson}
                  course={course}
                  commentData={comments}
                  onRender={onMarkdownRendered}
                  initialDraft={initialDraft}
                  onActiveTabChange={(activeTab) => {
                    if (activeTab === 'community') setShowBottomComments(false);
                    else setShowBottomComments(true);
                  }}
                />
              ) : (
                <div className={cx({'LessonPage--blocked': !hasAccess})}>
                  <Markdown
                    placeholder={<Placeholder />}
                    allowRaw
                    onRender={onMarkdownRendered}
                  >
                    {lesson.content}
                  </Markdown>
                </div>
              )}

            {/* Lesson footer */}
            <div className="flex flex-col-reverse sm:flex-row justify-between items-stretch sm:items-start mt-8">
              {showInteractiveElements && (
                <RatingWidget
                  initialRating={lesson.userRating}
                  onRate={onClickRateLesson}
                  onSubmitFeedback={onClickSubmitFeedback}
                  className="text-md font-medium text-gray-700 mt-5 mb-10 sm:mt-0 sm:mb-0"
                />
              )}

              {nextLessonPath && hasAccess && (
              <Button
                href={nextLessonPath}
                onClick={onClickNextLesson}
                size={ButtonSizes.LARGE}
              >
                Next Lesson
              </Button>
              )}
            </div>
          </div>

          {/* Table of contents */}
          {showToC && (
            <div
              className="relative hidden lg:block"
              style={{width: 200}}
            >
              <div className="sticky top-0">
                <TableOfContents
                  sections={toc}
                  scrollContainer={lessonContainerRef?.current || undefined}
                  header="Lesson contents"
                />
              </div>
            </div>
          )}
        </div>

        {/* Signup and upgrade walls */}
        {lesson.paywalledVideo && (
          <div className="relative rounded-lg overflow-hidden max-w-5xl mx-auto bg-gray-400" style={{height: 420}}>
            <video
              className="filter blur h-full w-full object-cover"
              style={{height: 420}}
              src={VIDEO_PREVIEW_URL}
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="absolute top-0 bottom-0 left-0 right-0 bg-black bg-opacity-75" />
          </div>
        )}
        <div className={cx('relative max-w-3xl mx-auto', {'-mt-64': lesson.paywalledVideo})}>
          {showSignupWall && (
            <SignupWall
              onView={() => tracker.lessonViewSignupWall({
                id: lessonSlug, lesson: lessonSlug, course: courseSlug, track: trackSlug,
              })}
              onClick={(type) => tracker.lessonClickSignupWall({
                type, lesson: lessonSlug, id: lessonSlug, course: courseSlug, track: trackSlug,
              })}
            />
          )}
          {showPaywall && !preview && (
            <LessonPaywall
              trackSlug={trackSlug}
              courseSlug={courseSlug}
              lessonSlug={lessonSlug}
              nextFreeLesson={getNextAvailableLesson({
                courses: courseList,
                trackSlug,
                hasSubscription: false,
                from: lessonSlug,
              })}
            />
          )}
          {lesson.rubric && hasAccess && (
            <InteractiveRubric
              lesson={lesson}
              trackSlug={trackSlug}
              courseSlug={courseSlug}
              lessonSlug={lessonSlug}
              showPaywall={showPaywall}
              showSignupWall={showSignupWall}
            />
          )}
        </div>

        {/* Comment section from linked question. Hide when Discussion Tab is being shown */}
        {showBottomComments && lesson.questionId && showInteractiveElements && (
          <LessonCommentSection
            questionID={lesson.questionId.toString()}
            commentData={comments}
            open={!!initialDraft}
            numAnswers={isInteractiveLesson ? 0 : 1}
            className="my-8 border-t-2 pt-8"
            ref={lessonCommentSectionRef}
          />
        )}
      </div>
    </div>
  );
};

const LessonPageWithContainer = (props) => (
  <CourseLessonsProvider>
    <LessonContainer
      trackSlug={props.trackSlug}
      courseSlug={props.courseSlug}
      lessonSlug={props.lessonSlug}
    >
      {({initialDraft}) => (
        <LessonPage {...props} initialDraft={initialDraft} />
      )}
    </LessonContainer>
  </CourseLessonsProvider>
);

export default LessonPageWithContainer;
